/**
 * React Query hooks for the Scheduled Agent Jobs admin API.
 *
 * Backend contract lives in:
 *   good-outcomes-api/docs/plans/2026-04-21-scheduled-agent-jobs-design.md
 *
 * Endpoints are under /api/admin/scheduled-jobs, organization-scoped and
 * admin-gated on the server. We call them via fetchWithAuth so auth headers
 * are attached automatically.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { fetchWithAuth } from '../utils/api';
import type {
  CreateScheduledJobPayload,
  ListScheduledJobRunsResponse,
  ListScheduledJobsResponse,
  ScheduledJob,
  ScheduledJobDetail,
  ScheduledJobRun,
  UpdateScheduledJobPayload,
} from '../types/scheduledJobs';

const BASE_PATH = '/v1/admin/scheduled-jobs';

const scheduledJobsKeys = {
  all: ['admin', 'scheduled-jobs'] as const,
  lists: () => [...scheduledJobsKeys.all, 'list'] as const,
  detail: (id: string) => [...scheduledJobsKeys.all, 'detail', id] as const,
  runs: (jobId: string) => [...scheduledJobsKeys.all, 'runs', jobId] as const,
  runsList: (jobId: string, limit: number, offset: number) =>
    [...scheduledJobsKeys.runs(jobId), { limit, offset }] as const,
  run: (jobId: string, runId: string) =>
    [...scheduledJobsKeys.runs(jobId), 'detail', runId] as const,
};

export const scheduledJobsQueryKeys = scheduledJobsKeys;

const invalidateJobLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: scheduledJobsKeys.lists() });
};

const invalidateJobDetail = (
  queryClient: ReturnType<typeof useQueryClient>,
  jobId: string,
) => {
  queryClient.invalidateQueries({ queryKey: scheduledJobsKeys.detail(jobId) });
  queryClient.invalidateQueries({ queryKey: scheduledJobsKeys.runs(jobId) });
};

/**
 * List all scheduled jobs for the current org. Auto-refetches every 10s
 * while any job has a run in-flight so the list stays live during demos.
 */
export const useScheduledJobs = () => {
  return useQuery<ListScheduledJobsResponse>({
    queryKey: scheduledJobsKeys.lists(),
    queryFn: async () =>
      (await fetchWithAuth(BASE_PATH)) as ListScheduledJobsResponse,
    refetchInterval: (query) => {
      const data = query.state.data as ListScheduledJobsResponse | undefined;
      const hasRunning = data?.items?.some(
        (job) => job.latest_run?.status === 'running' || job.latest_run?.status === 'pending',
      );
      return hasRunning ? 10000 : false;
    },
  });
};

/**
 * Fetch a single job, including recent runs. Refetches every 5s while a
 * run is in flight for the detail drawer.
 */
export const useScheduledJob = (id: string | null | undefined) => {
  return useQuery<ScheduledJobDetail>({
    queryKey: id ? scheduledJobsKeys.detail(id) : (['admin', 'scheduled-jobs', 'detail', 'nil'] as QueryKey),
    queryFn: async () =>
      (await fetchWithAuth(`${BASE_PATH}/${id}`)) as ScheduledJobDetail,
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data as ScheduledJobDetail | undefined;
      const hasRunning = data?.recent_runs?.some(
        (run) => run.status === 'running' || run.status === 'pending',
      );
      return hasRunning ? 5000 : false;
    },
  });
};

/**
 * Paginated run history for a given job.
 */
export const useScheduledJobRuns = (
  jobId: string | null | undefined,
  limit: number = 25,
  offset: number = 0,
) => {
  return useQuery<ListScheduledJobRunsResponse>({
    queryKey: jobId
      ? scheduledJobsKeys.runsList(jobId, limit, offset)
      : (['admin', 'scheduled-jobs', 'runs', 'nil'] as QueryKey),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      return (await fetchWithAuth(
        `${BASE_PATH}/${jobId}/runs?${params.toString()}`,
      )) as ListScheduledJobRunsResponse;
    },
    enabled: Boolean(jobId),
  });
};

/**
 * Single run detail (with full output/error). Polls every 2s while
 * status === 'running'.
 */
export const useScheduledJobRun = (
  jobId: string | null | undefined,
  runId: string | null | undefined,
) => {
  return useQuery<ScheduledJobRun>({
    queryKey:
      jobId && runId
        ? scheduledJobsKeys.run(jobId, runId)
        : (['admin', 'scheduled-jobs', 'run', 'nil'] as QueryKey),
    queryFn: async () =>
      (await fetchWithAuth(
        `${BASE_PATH}/${jobId}/runs/${runId}`,
      )) as ScheduledJobRun,
    enabled: Boolean(jobId && runId),
    refetchInterval: (query) => {
      const data = query.state.data as ScheduledJobRun | undefined;
      return data?.status === 'running' || data?.status === 'pending'
        ? 2000
        : false;
    },
  });
};

// ---------- Mutations ----------

export const useCreateScheduledJob = () => {
  const queryClient = useQueryClient();
  return useMutation<ScheduledJob, Error, CreateScheduledJobPayload>({
    mutationFn: async (payload) =>
      (await fetchWithAuth(BASE_PATH, {
        method: 'POST',
        body: JSON.stringify(payload),
      })) as ScheduledJob,
    onSuccess: () => invalidateJobLists(queryClient),
  });
};

export const useUpdateScheduledJob = () => {
  const queryClient = useQueryClient();
  return useMutation<
    ScheduledJob,
    Error,
    { id: string; payload: UpdateScheduledJobPayload }
  >({
    mutationFn: async ({ id, payload }) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })) as ScheduledJob,
    onSuccess: (_data, variables) => {
      invalidateJobLists(queryClient);
      invalidateJobDetail(queryClient, variables.id);
    },
  });
};

export const useDeleteScheduledJob = () => {
  const queryClient = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}`, {
        method: 'DELETE',
      })) as null,
    onSuccess: () => invalidateJobLists(queryClient),
  });
};

export const usePauseScheduledJob = () => {
  const queryClient = useQueryClient();
  return useMutation<ScheduledJob, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/pause`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ScheduledJob,
    onSuccess: (_data, id) => {
      invalidateJobLists(queryClient);
      invalidateJobDetail(queryClient, id);
    },
  });
};

export const useResumeScheduledJob = () => {
  const queryClient = useQueryClient();
  return useMutation<ScheduledJob, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/resume`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ScheduledJob,
    onSuccess: (_data, id) => {
      invalidateJobLists(queryClient);
      invalidateJobDetail(queryClient, id);
    },
  });
};

export const useRunScheduledJobNow = () => {
  const queryClient = useQueryClient();
  return useMutation<ScheduledJobRun, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/run-now`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ScheduledJobRun,
    onSuccess: (_data, id) => {
      invalidateJobLists(queryClient);
      invalidateJobDetail(queryClient, id);
    },
  });
};

export const useStopScheduledJobRun = () => {
  const queryClient = useQueryClient();
  return useMutation<ScheduledJobRun, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/stop`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ScheduledJobRun,
    onSuccess: (_data, id) => {
      invalidateJobLists(queryClient);
      invalidateJobDetail(queryClient, id);
    },
  });
};
